import CustomApplicationState from "@crescentParts/Store/CustomApplicationState";
import selectWarehouseForInventoryHandler from "@crescentParts/Store/Data/WarehouseData/Handler/SelectWarehouseForInventory";
import setIsTrainingProductInCartHandler from "@crescentParts/Store/Data/WarehouseData/Handler/SetIsTrainingProductInCartHandler";
import WarehouseDataHandler from "@crescentParts/Store/Data/WarehouseData/Handler/WarehouseDataHandler";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const cartData = getCurrentCartState(state).value;
    return {
        session: state.context.session,
        warehouseData: state.cpeState.warehouseData,
        defaultWarehouse: cartData?.defaultWarehouse?.name,
        selectedWarehouse: state.cpeState.warehouseData.selectedWarehouseForInventory,
        pageType: getCurrentPage(state).type,
        trainingWarehouseCart: cartData?.properties.trainingWarehouseCart,
        cartlines: cartData?.cartLines,
        isTrainingProductInCart: state.cpeState.warehouseData.isTrainingProductInCart,
    };
};

const mapDispatchToProps = {
    selectWarehouseForInventoryHandler,
    WarehouseDataHandler,
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    setIsTrainingProductInCartHandler,
};

export interface WarehouseMenuStyles {
    warehouseSelect?: SelectPresentationProps;
}

interface OwnProps {
    extendedStyles?: WarehouseMenuStyles;
}

export const warehouseMenuStyles: WarehouseMenuStyles = {
    warehouseSelect: {
        backgroundColor: "common.accent",
        cssOverrides: {
            formInputWrapper: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`
                            width: 118px;
                        `,
                        css`
                            width: 271px;
                        `,
                    ])}
            `,
            inputSelect: css`
                border: none;
                text-transform: uppercase;
            `,
        },
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const WarehouseSelectionMenu = ({
    session,
    warehouseData,
    defaultWarehouse,
    selectedWarehouse,
    pageType,
    trainingWarehouseCart,
    WarehouseDataHandler,
    selectWarehouseForInventoryHandler,
    updatePickUpWarehouse,
    extendedStyles,
    cartlines,
    isTrainingProductInCart,
    setIsTrainingProductInCartHandler,
}: Props) => {
    const styles = mergeToNew(warehouseMenuStyles, extendedStyles);
    const [warehouse, setWarehouse] = useState(selectedWarehouse || defaultWarehouse || "");
    const toasterContext = React.useContext(ToasterContext);

    const syncBrowserState = (warehouseName: string) => {
        if (typeof window === "undefined") {
            return;
        }

        const stored = localStorage.getItem("selectedWarehouseForInventory");

        if (stored !== warehouseName) {
            localStorage.setItem("selectedWarehouseForInventory", warehouseName);

            const url = new URL(window.location.href);
            if (!url.pathname.includes("/Training")) {
                url.searchParams.set("wh", warehouseName);
                window.history.replaceState({}, "", url.toString());
                window.dispatchEvent(new PopStateEvent("popstate"));
            }
        }
    };

    const handleWarehouseSync = async () => {
        if (!warehouseData?.warehouseData || !selectedWarehouse) {
            return;
        }

        const selectedWarehouseModel = warehouseData.warehouseData.find(
            warehouse => warehouse.name === selectedWarehouse,
        );

        if (!selectedWarehouseModel?.id) {
            return;
        }

        await updatePickUpWarehouse({
            pickUpWarehouse: { id: selectedWarehouseModel.id } as WarehouseModel,
        });

        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };

    useEffect(() => {
        if (!selectedWarehouse && defaultWarehouse) {
            selectWarehouseForInventoryHandler({
                selectedWarehouseForInventory: defaultWarehouse,
            });
        }
    }, [selectedWarehouse, defaultWarehouse, selectWarehouseForInventoryHandler]);

    useEffect(() => {
        const nextWarehouse = selectedWarehouse || defaultWarehouse || "";
        setWarehouse(nextWarehouse);

        if (selectedWarehouse) {
            syncBrowserState(selectedWarehouse);
        } else if (typeof window !== "undefined") {
            localStorage.removeItem("selectedWarehouseForInventory");
        }
    }, [selectedWarehouse, defaultWarehouse]);

    useEffect(() => {
        if (pageType === "CartPage" && trainingWarehouseCart) {
            if (!cartlines || cartlines.length === 0) {
                setIsTrainingProductInCartHandler({
                    isTrainingProductInCart: false,
                });
            } else {
                setIsTrainingProductInCartHandler({
                    isTrainingProductInCart: true,
                });
            }
        } else if (isTrainingProductInCart === true) {
            setIsTrainingProductInCartHandler({
                isTrainingProductInCart: false,
            });
        }
    }, [pageType, cartlines?.length]);

    useEffect(() => {
        if (!warehouseData?.warehouseData || !selectedWarehouse) {
            return;
        }

        if (typeof window !== "undefined" && pageType === "CartPage") {
            const params = new URLSearchParams(window.location.search);
            const isTrainingProduct = params.get("trainingPrd");
            const selectedTrainingWarehouse = params.get("wh");
            if (isTrainingProduct && selectedTrainingWarehouse) {
                selectWarehouseForInventoryHandler({
                    selectedWarehouseForInventory: selectedTrainingWarehouse,
                });
            } else if (trainingWarehouseCart) {
                selectWarehouseForInventoryHandler({
                    selectedWarehouseForInventory: trainingWarehouseCart,
                });
            }
        }

        if (
            session.fulfillmentMethod === FulfillmentMethod.PickUp &&
            session.pickUpWarehouse?.name !== selectedWarehouse
        ) {
            handleWarehouseSync();
        }
    }, [pageType, selectedWarehouse, warehouseData?.warehouseData, session.fulfillmentMethod]);

    useEffect(() => {
        WarehouseDataHandler({});
    }, [WarehouseDataHandler]);

    const handleWarehouseChange = (selectedWarehouseData: string) => {
        if (selectedWarehouseData === warehouse) {
            return;
        }

        selectWarehouseForInventoryHandler({
            selectedWarehouseForInventory: selectedWarehouseData,
        });
    };

    if (!warehouseData) {
        return null;
    }

    return (
        <Select
            {...styles.warehouseSelect}
            value={warehouse}
            disabled={isTrainingProductInCart}
            onChange={e => handleWarehouseChange(e.target.value)}
            data-test-selector="warehouse-selection-menu"
        >
            <option value="">{translate("Select Warehouse")}</option>
            {warehouseData?.warehouseData &&
                warehouseData?.warehouseData.map((data: any) => (
                    <option key={data.name} value={data.name}>
                        {data.description}
                    </option>
                ))}
        </Select>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WarehouseSelectionMenu);
